<template>
  <kt-modal
    :visible="visible"
    :title="isEditing ? 'Edit Status' : 'Add Status'"
    size="modal-lg"
    @dismiss="onClose"
  >
    <template #body>
      <form @submit.prevent="onSave">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Label <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              v-model="form.Label"
              placeholder="e.g., In Progress"
              required
            />
            <small class="form-text text-muted">Display name for this status</small>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Category</label>
            <input
              type="text"
              class="form-control"
              :value="category"
              disabled
            />
            <small class="form-text text-muted">Category cannot be changed</small>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Color</label>
            <div class="input-group">
              <input
                type="color"
                class="form-control form-control-color"
                v-model="form.Color"
                title="Choose color"
              />
              <input
                type="text"
                class="form-control"
                v-model="form.Color"
                placeholder="#3B9796"
                pattern="^#[0-9A-Fa-f]{6}$"
              />
            </div>
            <small class="form-text text-muted">Hex color for badges (e.g., #3B9796)</small>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Icon</label>
            <div class="input-group">
              <span class="input-group-text">
                <i v-if="form.Icon" :class="form.Icon"></i>
                <span v-else class="text-muted">-</span>
              </span>
              <input
                type="text"
                class="form-control"
                v-model="form.Icon"
                placeholder="fas fa-check"
              />
            </div>
            <small class="form-text text-muted">Font Awesome class (e.g., fas fa-check)</small>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Value <span class="text-danger">*</span></label>
            <input
              type="number"
              class="form-control"
              v-model.number="form.Value"
              min="0"
              required
            />
            <small class="form-text text-muted">Integer stored in database (e.g., 1, 2, 3)</small>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Sort Order</label>
            <input
              type="number"
              class="form-control"
              v-model.number="form.SortOrder"
              min="0"
            />
            <small class="form-text text-muted">Display order (0 = first)</small>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="isActive"
                v-model="form.IsActive"
              />
              <label class="form-check-label" for="isActive">
                Status is Active
              </label>
            </div>
            <small class="form-text text-muted d-block">
              Inactive statuses are hidden from selection
            </small>
          </div>
          <div class="col-md-6 mb-3">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="isRefActive"
                v-model="form.IsRefActive"
              />
              <label class="form-check-label" for="isRefActive">
                Entity remains Active
              </label>
            </div>
            <small class="form-text text-muted d-block">
              When OFF, entity is terminal (completed/cancelled)
            </small>
          </div>
        </div>

        <div class="alert alert-info">
          <h6 class="alert-heading">Status Configuration Help</h6>
          <ul class="mb-0 small">
            <li><strong>Active Status:</strong> Enable to make this status available for selection</li>
            <li><strong>Entity Active:</strong> Keep ON for open/in-progress statuses. Turn OFF for terminal statuses like "Completed" or "Cancelled"</li>
            <li><strong>Color:</strong> Used for visual badges in the UI</li>
            <li><strong>Sort Order:</strong> Determines position in dropdown lists (lower = first)</li>
          </ul>
        </div>
      </form>
    </template>

    <template #footer>
      <div class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-outline-secondary" @click="onClose">
          Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="onSave" :disabled="!isValid">
          <Save :size="16" class="me-1" />
          {{ isEditing ? 'Save Changes' : 'Create Status' }}
        </button>
      </div>
    </template>
  </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Save } from 'lucide-vue-next';
import KtModal from '@/components/UI/KtModal.vue';
import type { StatusDefinition, SaveStatusRequest } from '@/models/StatusDefinitionModel';

const props = defineProps<{
  visible: boolean;
  status: StatusDefinition | null;
  category: string;
}>();

const emit = defineEmits<{
  close: [];
  save: [status: SaveStatusRequest];
}>();

const isEditing = computed(() => props.status !== null);

const defaultForm: SaveStatusRequest = {
  Id: 0,
  Value: 0,
  Label: '',
  Category: '',
  IsActive: true,
  IsRefActive: true,
  SortOrder: 0,
  Color: '#6c757d',
  Icon: ''
};

const form = ref<SaveStatusRequest>({ ...defaultForm });

const isValid = computed(() => {
  return form.value.Label.trim().length > 0;
});

// Watch for status changes to populate form
watch(() => props.status, (newStatus) => {
  if (newStatus) {
    form.value = {
      Id: newStatus.Id,
      Value: newStatus.Value,
      Label: newStatus.Label,
      Category: newStatus.Category,
      IsActive: newStatus.IsActive,
      IsRefActive: newStatus.IsRefActive,
      SortOrder: newStatus.SortOrder,
      Color: newStatus.Color || '#6c757d',
      Icon: newStatus.Icon || ''
    };
  } else {
    form.value = {
      ...defaultForm,
      Category: props.category
    };
  }
}, { immediate: true });

// Watch for visibility changes
watch(() => props.visible, (isVisible) => {
  if (isVisible && !props.status) {
    // Reset form when opening for new status
    form.value = {
      ...defaultForm,
      Category: props.category
    };
  }
});

const onClose = () => {
  emit('close');
};

const onSave = () => {
  if (!isValid.value) return;
  
  // Ensure Category is set
  const saveData: SaveStatusRequest = {
    ...form.value,
    Category: props.category
  };
  
  emit('save', saveData);
};
</script>

<style scoped>
.form-control-color {
  padding: 0.375rem;
  width: 50px;
  cursor: pointer;
}

.form-switch .form-check-input {
  width: 2.5em;
  height: 1.25em;
}
</style>
